// A pool's session: kept where the person chose (this browser, or only this tab), the same in every tab, and renewed
// a minute before it expires. Renewing happens in one tab at a time, under a lock, and reads the session again once
// it has the lock, so a refresh token is never used twice (Cognito ends a session whose refresh token is reused).
import type { Pool } from '../config';
import { CognitoError, type AuthResult } from './cognito';
import { isTokens, tokensFrom, whoOf, type Tokens, type Who } from './session';

/** Where a session is kept: in this browser until it's signed out, or in this tab until it closes. */
export type Keep = 'browser' | 'tab';

/** The Web Locks API, or a stand-in for it. */
export interface Locks {
  request<T>(name: string, task: () => Promise<T>): Promise<T>;
}

/** BroadcastChannel, or a stand-in for it. */
export interface Channel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

export interface StoreDeps {
  local: Storage | null;
  session: Storage | null;
  locks: Locks;
  channel: Channel | null;
  now: () => number;
  renew(refreshToken: string): Promise<AuthResult>;
  revoke(refreshToken: string): Promise<unknown>;
}

export interface Saved {
  tokens: Tokens;
  keep: Keep;
}

/** Renewed this long before it expires. */
const EARLY_MS = 60_000;

/** Cognito's answers that mean the session can't go on; anything else (the network, a busy moment) can be tried again. */
const ENDED = new Set([
  'NotAuthorizedException',
  'RefreshTokenReuseException',
  'UserNotFoundException',
  'InvalidParameterException',
]);

export const sessionKey = (pool: Pool) => `tune.session.${pool}`;

/** Within this tab, one at a time: for a browser without Web Locks. */
export function tabLocks(): Locks {
  const queues = new Map<string, Promise<unknown>>();
  return {
    request<T>(name: string, task: () => Promise<T>): Promise<T> {
      const before = queues.get(name) ?? Promise.resolve();
      const run = before.then(task, task);
      queues.set(
        name,
        run.catch(() => undefined),
      );
      return run;
    },
  };
}

export class SessionStore {
  private saved: Saved | null;
  private readonly listeners = new Set<() => void>();

  constructor(
    readonly pool: Pool,
    private readonly deps: StoreDeps,
  ) {
    this.saved = this.read();
    deps.channel?.addEventListener('message', (e: MessageEvent) => {
      if ((e.data as { pool?: string } | null)?.pool === pool) {
        this.reload();
      }
    });
  }

  private read(): Saved | null {
    const places: Array<[Storage | null, Keep]> = [
      [this.deps.local, 'browser'],
      [this.deps.session, 'tab'],
    ];
    for (const [storage, keep] of places) {
      try {
        const raw = storage?.getItem(sessionKey(this.pool));
        const tokens: unknown = raw ? JSON.parse(raw) : null;
        if (isTokens(tokens)) {
          return { tokens, keep };
        }
      } catch {
        // Storage that can't be read keeps nothing.
      }
    }
    return null;
  }

  private write(saved: Saved | null): void {
    const key = sessionKey(this.pool);
    for (const [storage, keep] of [
      [this.deps.local, 'browser'],
      [this.deps.session, 'tab'],
    ] as const) {
      try {
        if (saved && saved.keep === keep) {
          storage?.setItem(key, JSON.stringify(saved.tokens));
        } else {
          storage?.removeItem(key);
        }
      } catch {
        // Storage that's full or blocked: the session lasts while the page does.
      }
    }
  }

  private apply(saved: Saved | null): void {
    const same = saved?.tokens.accessToken === this.saved?.tokens.accessToken && saved?.keep === this.saved?.keep;
    if (!same) {
      this.saved = saved;
      this.listeners.forEach((l) => l());
    }
  }

  private broadcast(): void {
    try {
      this.deps.channel?.postMessage({ pool: this.pool });
    } catch {
      // A closed channel: other tabs find out from storage.
    }
  }

  /** Reads the session again: another tab signed in, out, or renewed it. */
  reload(): void {
    this.apply(this.read());
  }

  current(): Saved | null {
    return this.saved;
  }

  who(): Who | null {
    return this.saved ? whoOf(this.saved.tokens) : null;
  }

  /** Signed in: keeps the session where the person chose, and tells the other tabs. */
  signedIn(result: AuthResult, keep: Keep): void {
    const saved = { tokens: tokensFrom(result, undefined, this.deps.now()), keep };
    this.write(saved);
    this.apply(saved);
    this.broadcast();
  }

  /**
   * Kept in this browser, or only in this tab: the person changed their mind. Other tabs aren't told, so a tab kept
   * open stays signed in until it next renews its session.
   */
  keepIn(keep: Keep): void {
    if (!this.saved || this.saved.keep === keep) {
      return;
    }
    const next = { tokens: this.saved.tokens, keep };
    this.write(next);
    this.apply(next);
  }

  /** Signed out: forgets the session here and in every tab, then asks Cognito to end its refresh token. */
  async signOut(): Promise<void> {
    const was = this.saved;
    this.write(null);
    this.apply(null);
    this.broadcast();
    if (was) {
      await this.deps.revoke(was.tokens.refreshToken).catch(() => undefined);
    }
  }

  /**
   * An access token good for a minute more, renewed first when it isn't; `force` renews it anyway (after the API
   * refused it). Null once signed out.
   */
  async accessToken(force = false): Promise<string | null> {
    const t = this.saved?.tokens;
    if (!t) {
      return null;
    }
    if (!force && t.expiresAt - this.deps.now() > EARLY_MS) {
      return t.accessToken;
    }
    const seen = t.refreshToken;
    return this.deps.locks.request(`tune-refresh-${this.pool}`, async () => {
      const latest = this.read();
      if (!latest) {
        this.apply(null);
        return null;
      }
      // Another tab renewed it while this one waited for the lock.
      if (latest.tokens.refreshToken !== seen || (!force && latest.tokens.expiresAt - this.deps.now() > EARLY_MS)) {
        this.apply(latest);
        return latest.tokens.accessToken;
      }
      try {
        const result = await this.deps.renew(latest.tokens.refreshToken);
        const next = { tokens: tokensFrom(result, latest.tokens.refreshToken, this.deps.now()), keep: latest.keep };
        this.write(next);
        this.apply(next);
        this.broadcast();
        return next.tokens.accessToken;
      } catch (e) {
        if (e instanceof CognitoError && ENDED.has(e.type)) {
          this.write(null);
          this.apply(null);
          this.broadcast();
          return null;
        }
        // The network or Cognito had a moment: the token goes on while it's good.
        return latest.tokens.expiresAt > this.deps.now() ? latest.tokens.accessToken : null;
      }
    });
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Saved | null => this.saved;
}
